/**
 * Controller for the custom-amount donation form.
 *
 * The donation product is priced at one currency unit (e.g. £1) per item, so a
 * custom donation is added as quantity = amount / unit price. This element keeps
 * the hidden `quantity` field, the preset buttons and the submit label in sync
 * with whatever amount the customer chooses. The actual add-to-cart is handled
 * by the theme's <product-form-component>, so the cart drawer and impact meter
 * update automatically.
 */
class CsDonationForm extends HTMLElement {
  connectedCallback() {
    /** Unit price in minor units (pence). Defaults to £1.00. */
    this.unit = parseInt(this.dataset.unit || '100', 10) || 100;
    this.min = parseFloat(this.dataset.min || '1') || 1;
    this.symbol = this.dataset.symbol || '£';
    this.donateLabel = this.dataset.donateLabel || 'Donate {amount}';

    this.amountInput = this.querySelector('[data-donation-amount]');
    this.quantityInput = this.querySelector('[data-donation-quantity]');
    this.buttonTextEl = this.querySelector('.add-to-cart-text__content span span');
    this.presetButtons = Array.from(this.querySelectorAll('[data-donation-preset]'));

    this.presetButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const value = parseFloat(button.dataset.donationPreset || '0');
        this.setAmount(value, true);
      });
    });

    if (this.amountInput) {
      this.amountInput.addEventListener('input', () => {
        this.setAmount(parseFloat(this.amountInput.value), false);
      });
    }

    // Initialise from the current input value, or the first preset / minimum.
    const initial =
      parseFloat(this.amountInput?.value) ||
      parseFloat(this.presetButtons[0]?.dataset.donationPreset || '') ||
      this.min;
    this.setAmount(initial, true);
  }

  /**
   * @param {number} amountMajor - Donation amount in major units (e.g. pounds).
   * @param {boolean} writeInput - Whether to write the value back into the input.
   */
  setAmount(amountMajor, writeInput) {
    const valid = Number.isFinite(amountMajor) && amountMajor >= this.min;
    const amount = valid ? amountMajor : 0;

    if (writeInput && this.amountInput && valid) {
      this.amountInput.value = String(amount);
    }

    const quantity = valid ? Math.max(1, Math.round((amount * 100) / this.unit)) : 0;
    if (this.quantityInput) this.quantityInput.value = String(quantity);

    // Highlight a matching preset, if any.
    this.presetButtons.forEach((button) => {
      const isActive = valid && parseFloat(button.dataset.donationPreset || '') === amount;
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      button.classList.toggle('is-active', isActive);
    });

    // Update the submit button label and disabled state.
    if (this.buttonTextEl) {
      const formatted = valid ? `${this.symbol}${this.formatAmount(amount)}` : '';
      this.buttonTextEl.textContent = valid
        ? this.donateLabel.replace('{amount}', formatted)
        : this.donateLabel.replace('{amount}', '').trim();
    }

    const button = this.querySelector('button[name="add"]');
    if (button) button.disabled = !valid;
  }

  /** Formats an amount without trailing .00 for whole numbers. */
  formatAmount(amount) {
    return Number.isInteger(amount) ? String(amount) : amount.toFixed(2);
  }
}

if (!customElements.get('cs-donation-form')) {
  customElements.define('cs-donation-form', CsDonationForm);
}
